'use client';

import clsx from 'clsx';
import { startTransition, useOptimistic, useState } from 'react';

import { useUpdateTaskMutation } from '@/features/tasks/api/tasksApi';
import {
  getTaskStatusLabel,
  getTaskStatusTone,
} from '@/features/tasks/utils/taskStatusDisplay';
import { Badge, Popover } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './UpdateTaskStatus.module.scss';

import type { TaskStatus } from '@/features/tasks/types';

type UpdateTaskStatusProps = {
  taskId: string;
  value: TaskStatus;
};

const STATUS_OPTIONS: Array<{ value: TaskStatus; label: string }> = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export function UpdateTaskStatus(props: UpdateTaskStatusProps) {
  return (
    <UpdateTaskStatusInner key={`${props.taskId}:${props.value}`} {...props} />
  );
}

function UpdateTaskStatusInner({ taskId, value }: UpdateTaskStatusProps) {
  const [updateTask, { isLoading }] = useUpdateTaskMutation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [committedStatus, setCommittedStatus] = useState<TaskStatus>(value);
  const [optimisticStatus, setOptimisticStatus] =
    useOptimistic(committedStatus);

  const isOpen = Boolean(anchorEl);

  const handleSelectStatus = async (nextStatus: TaskStatus) => {
    const previousStatus = optimisticStatus;

    if (nextStatus === previousStatus) {
      setAnchorEl(null);
      return;
    }

    startTransition(() => {
      setOptimisticStatus(nextStatus);
    });
    setCommittedStatus(nextStatus);
    setAnchorEl(null);

    try {
      await updateTask({
        taskId,
        payload: {
          status: nextStatus,
        },
      }).unwrap();
    } catch (error) {
      startTransition(() => {
        setOptimisticStatus(previousStatus);
      });
      setCommittedStatus(previousStatus);
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <span className={styles.updateTaskStatus}>
      <button
        type="button"
        aria-label="Update task status"
        className={styles.updateTaskStatus__trigger}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Badge
          label={getTaskStatusLabel(optimisticStatus)}
          tone={getTaskStatusTone(optimisticStatus)}
          size="lg"
        />
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <div className={styles.updateTaskStatus__menu} role="menu">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              aria-label={`Set status ${option.label}`}
              className="action-item"
              disabled={isLoading}
              onClick={() => {
                void handleSelectStatus(option.value);
              }}
            >
              {option.label}
            </button>
          ))}
          {isLoading ? (
            <p className={styles.updateTaskStatus__hint}>Updating...</p>
          ) : null}
        </div>
      </Popover>
    </span>
  );
}
